import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BellRing, CheckCheck, CircleDot, Search, UserCheck } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackNotifications = [
  {
    _id: "preview-1",
    title: "Juki line price review",
    message: "Quarterly genuine parts price update is ready for review.",
    type: "pricing",
    priority: "medium",
    status: "open",
    owner: "Admin",
    createdAt: new Date().toISOString(),
  },
  {
    _id: "preview-2",
    title: "SKU PEG-2072 below threshold",
    message: "Needle Plate Assembly is close to reorder threshold.",
    type: "inventory",
    priority: "high",
    status: "open",
    owner: "Inventory",
    createdAt: new Date().toISOString(),
  },
];

const emptyAlert = {
  title: "",
  message: "",
  type: "system",
  priority: "medium",
  owner: "Operations",
};

function priorityTone(priority) {
  if (priority === "critical") return "bad";
  if (priority === "high") return "warn";
  if (priority === "low") return "neutral";
  return "info";
}

function statusTone(status) {
  if (status === "resolved") return "good";
  if (status === "reviewed") return "info";
  return "warn";
}

function isPreview(id) {
  return String(id || "").startsWith("preview");
}

function ageLabel(date) {
  const diffMs = Date.now() - new Date(date || Date.now()).getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes || 1}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationsCenter() {
  const [activeType, setActiveType] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [alertForm, setAlertForm] = useState(emptyAlert);
  const { dismiss, toast, toasts } = useToast();

  const loadNotifications = useCallback(async () => {
    const params = new URLSearchParams({ type: activeType, status, search, limit: "100" });
    const data = await api.get(`/notifications?${params.toString()}`);
    return data.notifications.length ? data.notifications : fallbackNotifications;
  }, [activeType, search, status]);

  const {
    data: notifications,
    error,
    loading,
    reload,
    setData: setNotifications,
  } = useAdminResource(loadNotifications, fallbackNotifications);

  useEffect(() => {
    if (error && !loading) toast(`Using preview notifications: ${error}`, "warning");
  }, [error, loading, toast]);

  const metrics = useMemo(() => ({
    open: notifications.filter((item) => item.status === "open").length,
    critical: notifications.filter((item) => item.priority === "critical").length,
    reviewed: notifications.filter((item) => item.status === "reviewed").length,
    resolved: notifications.filter((item) => item.status === "resolved").length,
  }), [notifications]);

  const typeCounts = useMemo(() => {
    return notifications.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    }, {});
  }, [notifications]);
  const notificationPagination = usePagination(notifications, [activeType, status, search], 10);

  const updateAlertForm = (event) => {
    setAlertForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const replaceNotification = (nextNotification) => {
    setNotifications((current) => current.map((item) => (item._id === nextNotification._id ? nextNotification : item)));
  };

  const createAlert = async (event) => {
    event.preventDefault();
    try {
      const data = await api.post("/notifications", alertForm);
      setNotifications((current) => [data.notification, ...current.filter((item) => !isPreview(item._id))]);
      setAlertForm(emptyAlert);
      setCreateOpen(false);
      toast("Notification created", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const updateNotification = async (notification, patch) => {
    if (isPreview(notification._id)) return;
    try {
      const data = await api.patch(`/notifications/${notification._id}`, patch);
      replaceNotification(data.notification);
      toast("Notification updated", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const bulkUpdate = async (nextStatus) => {
    const ids = selectedIds.filter((id) => !isPreview(id));
    if (!ids.length) return;
    try {
      await api.patch("/notifications/bulk", { notificationIds: ids, status: nextStatus });
      setNotifications((current) => current.map((item) => ids.includes(item._id) ? { ...item, status: nextStatus } : item));
      setSelectedIds([]);
      toast(`Marked ${ids.length} alerts ${nextStatus}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Notifications Center</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Operational alerts and exception workflow</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Review order issues, sync errors, low stock, shipping exceptions, return tasks, and system alerts from one queue.
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: "Refresh", onClick: reload },
            { label: "Mark Reviewed", onClick: () => bulkUpdate("reviewed") },
            { label: "Create Alert", primary: true, onClick: () => setCreateOpen((open) => !open) },
          ]}
        />
      </div>

      {createOpen && (
        <form className="grid gap-3 rounded-2xl bg-white p-6 shadow-sm md:grid-cols-2" onSubmit={createAlert}>
          <input className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="title" onChange={updateAlertForm} placeholder="Alert title" required value={alertForm.title} />
          <input className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="owner" onChange={updateAlertForm} placeholder="Owner" value={alertForm.owner} />
          <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="type" onChange={updateAlertForm} value={alertForm.type}>
            {["order", "inventory", "sync", "shipping", "return", "pricing", "system"].map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="priority" onChange={updateAlertForm} value={alertForm.priority}>
            {["low", "medium", "high", "critical"].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
          </select>
          <textarea className="min-h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold md:col-span-2" name="message" onChange={updateAlertForm} placeholder="Message" required value={alertForm.message} />
          <button className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600 md:col-span-2" type="submit">Create Notification</button>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InsightCard title="Open Alerts" value={metrics.open} note="Notifications needing action" tone="warn" />
        <InsightCard title="Critical" value={metrics.critical} note="Blocking sales or fulfillment" tone="warn" />
        <InsightCard title="Reviewed" value={metrics.reviewed} note="Acknowledged by team" tone="info" />
        <InsightCard title="Resolved" value={metrics.resolved} note="Closed operational alerts" tone="good" />
      </div>

      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="grid gap-3 xl:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <input className="w-full rounded-xl border border-gray-200 px-4 py-3 pl-11 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setSearch(event.target.value)} placeholder="Search alerts, owners, types" value={search} />
            <Search className="absolute left-4 top-3.5 text-gray-400" size={16} />
          </div>
          <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setStatus(event.target.value)} value={status}>
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
          </select>
          <div className="flex flex-wrap gap-2">
            {["all", "order", "inventory", "sync", "shipping", "return", "pricing", "system"].map((type) => (
              <button
                className={`rounded-xl px-3 py-2 text-xs font-black capitalize ${activeType === type ? "bg-[#4391d1] text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                key={type}
                onClick={() => setActiveType(type)}
                type="button"
              >
                {type} {typeCounts[type] ? `(${typeCounts[type]})` : ""}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BellRing className="text-[#4391d1]" />
            <h2 className="text-xl font-black text-gray-900">Notification Feed</h2>
          </div>
          <div className="flex gap-2">
            <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:text-[#4391d1]" onClick={() => bulkUpdate("reviewed")} type="button">
              <CheckCheck size={15} /> Review Selected
            </button>
            <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:text-[#4391d1]" onClick={() => bulkUpdate("resolved")} type="button">
              <CircleDot size={15} /> Resolve Selected
            </button>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
            <thead>
              <tr className="text-xs font-black uppercase text-gray-400">
                {["", "Alert", "Type", "Priority", "Owner", "Created", "Status", "Actions"].map((column) => (
                  <th className="px-3 py-3" key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notificationPagination.paginatedItems.map((notification) => (
                <tr className={notification.priority === "critical" ? "bg-red-50/50" : ""} key={notification._id}>
                  <td className="px-3 py-4">
                    <input checked={selectedIds.includes(notification._id)} onChange={() => toggleSelected(notification._id)} type="checkbox" />
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-start gap-3">
                      {notification.priority === "critical" ? <AlertTriangle className="mt-1 text-red-600" size={18} /> : <BellRing className="mt-1 text-[#4391d1]" size={18} />}
                      <div>
                        <p className="font-black text-gray-900">{notification.title}</p>
                        <p className="mt-1 max-w-xl text-xs font-semibold text-gray-500">{notification.message}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 font-bold capitalize text-gray-600">{notification.type}</td>
                  <td className="px-3 py-4"><StatusPill tone={priorityTone(notification.priority)}>{notification.priority}</StatusPill></td>
                  <td className="px-3 py-4">
                    <input
                      className="w-28 rounded-lg border border-gray-200 px-2 py-2 text-xs font-bold"
                      disabled={isPreview(notification._id)}
                      onBlur={(event) => updateNotification(notification, { owner: event.target.value })}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                      placeholder="Owner"
                      defaultValue={notification.owner}
                    />
                  </td>
                  <td className="px-3 py-4 font-semibold text-gray-500">{ageLabel(notification.createdAt)}</td>
                  <td className="px-3 py-4"><StatusPill tone={statusTone(notification.status)}>{notification.status}</StatusPill></td>
                  <td className="px-3 py-4">
                    <div className="flex flex-wrap gap-2">
                      <button className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:text-[#4391d1]" disabled={isPreview(notification._id)} onClick={() => updateNotification(notification, { status: "reviewed" })} type="button">
                        Review
                      </button>
                      <button className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:text-emerald-700" disabled={isPreview(notification._id)} onClick={() => updateNotification(notification, { status: "resolved" })} type="button">
                        <UserCheck size={14} /> Resolve
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={notificationPagination} />
      </section>
    </div>
  );
}
